import * as RadDialog from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import React from 'react'
import styled, { css, keyframes } from 'styled-components'

import { Button, CloseIcon } from '@blade/ui-kit'

import { DialogOverlay } from './dialog-overlay'
import { DialogHeader } from './header'

import type { DialogVariant } from './dialog-variant'
import type { ClassNameProps } from '@root/shared/react'

export type DialogControlProps = Pick<
  RadDialog.DialogProps,
  'onOpenChange' | 'open'
>

export type DialogProps = {
  describedBy: string
  open?: boolean
  variant?: DialogVariant
  children: React.ReactNode
  withClose?: boolean
  modal?: boolean
} & ClassNameProps &
  DialogControlProps

export const Dialog = (props: DialogProps) => {
  const handleClose = () => props.onOpenChange?.(false)

  return (
    <Root open={props.open} onOpenChange={handleClose} modal={props.modal}>
      <RadDialog.Portal>
        <DialogOverlay />
        <Body
          $variant={props.variant}
          aria-describedby={props.describedBy}
          className={props.className}
        >
          <VisuallyHidden.Root>
            <RadDialog.DialogTitle>{props.describedBy}</RadDialog.DialogTitle>
            <RadDialog.Description>{props.describedBy}</RadDialog.Description>
          </VisuallyHidden.Root>
          {props.withClose ? (
            <ButtonClose
              size="xs"
              variant="ghost"
              iconRight={<CloseIcon />}
              onClick={handleClose}
            />
          ) : undefined}
          {props.children}
        </Body>
      </RadDialog.Portal>
    </Root>
  )
}

const Root = styled(RadDialog.Root)`
  // Safari performance enhancement
  will-change: transform, opacity;
`

const ButtonClose = styled(Button)`
  color: var(--textA-11);

  position: absolute;
  right: 6px;
  top: 8px;

  &:hover {
    color: var(--text-12);
    background: transparent;
  }
`

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`

export const centeredDialogContentCss = css`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);

  &:focus {
    outline: none;
  }
`

const Body = styled(RadDialog.Content)<{ $variant?: DialogVariant }>`
  border: 1px solid var(--background-3);
  background-color: ${(x) =>
    x.$variant === 'functional'
      ? 'var(--background-2)'
      : 'var(--background-1)'};
  border-radius: 12px;

  overflow: hidden;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  box-shadow: ${(x) =>
    x.$variant === 'bordered' ? '0px 0px 0px 4px var(--background-1)' : 'none'};

  // Dialog positioning
  ${centeredDialogContentCss}

  max-height: calc(100% - 40px);
  max-width: calc(100% - 40px);
  overflow-y: auto;
`

const PlainBody = styled(RadDialog.Content)`
  border: 1px solid var(--background-3);
  background-color: var(--background-1);
  border-radius: 12px;
  overflow: hidden;

  animation: none;

  &:focus {
    outline: none;
  }
`

type PlainBodyProps = { describedBy: string } & React.ComponentProps<
  typeof PlainBody
>

type FunctionalContentVariant = 0 | 1

const FunctionalContentVariantToBackground: Record<
  FunctionalContentVariant,
  string
> = {
  0: 'var(--appearance-1)',
  1: 'var(--backgroundA-3)',
}

const FunctionalContent = styled.div<{
  $variant: FunctionalContentVariant
}>`
  border-radius: 12px;
  background: ${(x) => FunctionalContentVariantToBackground[x.$variant]};
  display: grid;
`

Dialog.Header = DialogHeader
Dialog.Content = { Functional: FunctionalContent }
Dialog.PlainBody = (props_: PlainBodyProps) => {
  const { children, describedBy, ...props } = props_

  return (
    <PlainBody {...props}>
      <VisuallyHidden.Root>
        <RadDialog.DialogTitle>{describedBy}</RadDialog.DialogTitle>
        <RadDialog.Description>{describedBy}</RadDialog.Description>
      </VisuallyHidden.Root>
      {children}
    </PlainBody>
  )
}
Dialog.Root = Root
